import axios from 'axios'
import { installAppVersionGuard } from '@/utils/appVersion'
import { installAuthGuard } from '@/utils/authSession'
import request from '@/utils/request'
import { API_BASE_URL, TOKEN_KEY } from '@/config/constants'

const imageResultRequest = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  responseType: 'blob',
  validateStatus: status => [200, 202, 204, 410].includes(status)
})

installAppVersionGuard(imageResultRequest)
installAuthGuard(imageResultRequest)

imageResultRequest.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY)
  if (token) {
    config.headers.satoken = token.startsWith('Bearer ') ? token.substring(7) : token
  }
  return config
})

/**
 * 获取视频列表
 * @param {Object} params - 查询参数
 * @param {number} params.current - 当前页码，默认1
 * @param {number} params.size - 每页数量，默认10
 * @param {string} [params.status] - 状态过滤：pending, queued, in_progress, completed, failed
 * @returns {Promise}
 */
export function getVideoRecords(params) {
  return request({
    url: '/api/video/records',
    method: 'get',
    params: {
      current: params.current || 1,
      size: params.size || 10,
      // 透传工作空间ID（如果有选择）
      workspaceId: params.workspaceId,
      // 状态过滤（传 'all' 或不传表示全部）
      status: params.status !== 'all' ? params.status : undefined,
      // 平台过滤（传 'all' 或不传表示全部）
      platform: params.platform !== 'all' ? params.platform : undefined
    }
  })
}

/**
 * 获取视频详情
 * @param {number|string} id - 视频ID
 * @returns {Promise}
 */
export function getVideoDetail(id) {
  return request({
    url: `/api/video/records/${id}`,
    method: 'get'
  })
}

/**
 * 查询视频生成进度
 * @param {string} taskId - 任务ID
 * @returns {Promise}
 */
export function getVideoProgress(taskId) {
  return request({
    url: '/api/video/progress',
    method: 'post',
    data: {
      taskId: taskId
    }
  })
}

/**
 * 查询 Seedance 视频生成进度（GET 方式）
 * @param {string} taskId - 任务ID
 * @returns {Promise}
 */
export function getSeedanceProgress(taskId) {
  return request({
    url: '/api/video/progress',
    method: 'get',
    params: {
      taskId: taskId
    }
  })
}

/**
 * 生成视频
 * @param {Object} data - 生成数据
 * @param {string} data.prompt - 提示词
 * @returns {Promise}
 */
export function generateVideo(data) {
  return request({
    url: '/api/video/generate',
    method: 'post',
    data,
    timeout: 180000
  })
}

export function acceptVideoGeneration(data, options = {}) {
  return request({ ...options, url: '/api/video/generation/accept', method: 'post', data, timeout: 30000 })
}

export function submitAcceptedVideoGeneration(data, options = {}) {
  return request({ ...options, url: '/api/video/generation/submit', method: 'post', data, timeout: 180000 })
}

export function failAcceptedVideoGeneration(data) {
  return request({ url: '/api/video/generation/fail', method: 'post', data, timeout: 30000, silent: true })
}

export function getVideoGenerationStatus(generationId, options = {}) {
  return request({ ...options, url: '/api/video/generation/status', method: 'get', params: { generationId } })
}

export function getVideoReferenceUploadConfig() {
  return request({ url: '/api/file/minio/video-reference/config', method: 'get' })
}

export function pollVideoReferenceUpload(queueId, options = {}) {
  return request({ ...options, url: '/api/file/minio/video-reference/queue/poll', method: 'post', params: { queueId }, silent: true })
}

export function releaseVideoReferenceUpload(queueId) {
  return request({ url: '/api/file/minio/video-reference/queue/release', method: 'post', params: { queueId }, silent: true })
}

export function getVideoReferenceSignature(params) {
  return request({ url: '/api/file/minio/video-reference/signature', method: 'post', params, silent: true })
}

export function completeVideoReferenceUpload(objectName) {
  return request({ url: '/api/file/minio/video-reference/complete', method: 'post', params: { objectName }, silent: true })
}

export function uploadVideoReferenceThroughBackend(file, category, queueId, onUploadProgress) {
  const form = new FormData()
  form.append('file', file)
  form.append('category', category)
  form.append('queueId', queueId)
  return request({ url: '/api/file/minio/video-reference/upload', method: 'post', data: form, timeout: 300000, onUploadProgress })
}

/**
 * 删除视频
 * @param {number|string} id - 视频ID
 * @returns {Promise}
 */
export function deleteVideo(id) {
  return request({
    url: `/api/video/records/${id}`,
    method: 'delete'
  })
}

/**
 * 删除图片生成记录
 * @param {number|string} id - 记录ID
 * @returns {Promise}
 */
export function deleteImageRecord(id) {
  return request({
    url: `/api/video/records/${id}`,
    method: 'delete'
  })
}

/**
 * 重新生成视频（Remix）
 * @param {Object} data - 重新生成数据
 * @param {string} data.originalTaskId - 原始任务ID
 * @param {string} data.prompt - 新的提示词
 * @returns {Promise}
 */
export function remixVideo(data) {
  return request({
    url: '/api/video/remix',
    method: 'post',
    data
  })
}

/**
 * 获取当前用户某一天的生成统计
 * @param {number} dayMillis - 当天任意时间的时间戳（建议使用当天12点）
 * @returns {Promise}
 */
export function getMyDailyStatistics(dayMillis) {
  return request({
    url: '/api/video/records/statistics/my-daily',
    method: 'get',
    params: {
      dayMillis
    }
  })
}

/**
 * 更新视频备注
 * @param {number|string} id - 视频ID
 * @param {string} remark - 备注内容
 * @returns {Promise}
 */
export function updateVideoRemark(id, remark) {
  return request({
    url: `/api/video/records/${id}/remark`,
    method: 'put',
    data: {
      remark
    }
  })
}

/**
 * 更新视频记录的videoUrl（前端转存OSS后回写）
 * @param {number|string} id - 记录ID
 * @param {string} videoUrl - 新的视频URL
 * @returns {Promise}
 */
export function updateVideoUrl(id, videoUrl) {
  return request({
    url: `/api/video/records/${id}/remark`,
    method: 'put',
    data: {
      videoUrl
    }
  })
}

/**
 * 生成图片
 * @param {Object} data - 生成数据
 * @param {string} [data.platform] - 图片生成平台，默认 yijia
 * @param {string} data.prompt - 提示词
 * @param {string} data.model - 模型类型：nano_banana_2、nano_banana_pro、image2、gpt-image-2.5
 * @param {string} [data.quality] - 画质选择：1k、2k、4k（可选，nano_banana系列模型适用）
 * @param {string[]} [data.images] - 参考图片的公开URL链接数组（可选）
 * @param {string} [data.size] - 图片尺寸，如 1024x1024（可选）
 * @param {number} [data.workspaceId] - 工作空间ID（可选）
 * @param {string} [data.remark] - 备注信息（可选）
 * @returns {Promise}
 */
export function generateImage(data) {
  return request({
    url: '/api/video/image/generate',
    method: 'post',
    data: {
      platform: data.platform || 'yijia',
      model: data.model,
      quality: data.quality || '',
      prompt: data.prompt,
      promptPrefix: data.promptPrefix || '',
      promptMain: data.promptMain || '',
      promptSuffix: data.promptSuffix || '',
      categoryId: data.categoryId || 4,
      image: (data.images && data.images[0]) || data.image || '',
      images: data.images || [],
      size: data.size || '',
      workspaceId: data.workspaceId,
      remark: data.remark,
      scriptId: data.scriptId
    },
    timeout: 30000
  })
}

/**
 * 查询图片生成进度（使用与视频相同的进度查询接口）
 * @param {string} taskId - 任务ID
 * @returns {Promise}
 */
export function getImageProgress(taskId) {
  return request({
    url: '/api/video/progress',
    method: 'post',
    data: {
      taskId: taskId
    }
  })
}

/**
 * 从数据库查询图片生成进度（不调用第三方API）
 * @param {string} taskId - 任务ID
 * @returns {Promise}
 */
export function getImageProgressLocal(taskId, options = {}) {
  return request({
    ...options,
    url: '/api/video/progress/local',
    method: 'get',
    params: { taskId }
  })
}

export function retryImageGeneration(taskId, expectedUpdatedAt) {
  return request({
    url: '/api/video/image/retry', method: 'post', silent: true,
    data: { taskId, expectedUpdatedAt }
  })
}

/**
 * 获取 Change2Pro 已生成但尚未持久化的图片二进制。
 * @param {string} taskId - 任务ID
 * @returns {Promise<import('axios').AxiosResponse<Blob>>}
 */
export function getGeneratedImageResult(taskId) {
  return imageResultRequest.get('/api/video/image/result', {
    params: { taskId }
  })
}

/**
 * 回写前端直传 OSS 后的正式图片地址。
 * @param {string} taskId - 任务ID
 * @param {string} imageUrl - OSS 图片地址
 * @returns {Promise}
 */
export function completeGeneratedImageUpload(taskId, imageUrl, options = {}) {
  return request({
    ...options,
    url: '/api/video/image/result/complete',
    method: 'post',
    data: { taskId, imageUrl }
  })
}

/**
 * 获取图片生成记录列表
 * @param {Object} params - 查询参数
 * @param {number} params.current - 当前页码，默认1
 * @param {number} params.size - 每页数量，默认10
 * @param {number} [params.workspaceId] - 工作空间ID（可选）
 * @returns {Promise}
 */
export function uploadLocalImage(file, context, onUploadProgress, signal) {
  const data = new FormData()
  data.append('file', file); data.append('scriptId', context.scriptId); data.append('categoryId', context.categoryId); data.append('requestId', context.requestId)
  return request({ url: '/api/video/image/local-upload', method: 'post', data, onUploadProgress, signal, timeout: 180000, silent: true, expectedAuthToken: context.authToken })
}

export function getImageRecords(params, options = {}) {
  return request({
    ...options,
    url: '/api/video/image/records',
    method: 'get',
    params: {
      current: params.current || 1,
      size: params.size || 10,
      workspaceId: params.workspaceId,
      scriptId: params.scriptId,
      categoryId: params.categoryId
    }
  })
}

export function uploadVideoReferenceStream(file, category, queueId, onUploadProgress, options = {}) {
  return request({ ...options, url: '/api/file/minio/video-reference/upload-stream', method: 'put',
    params: { category, queueId, fileName: file.name, fileSize: file.size, contentType: file.type || 'application/octet-stream' },
    data: file, headers: { 'Content-Type': 'application/octet-stream' }, timeout: 1800000, onUploadProgress, silent: true })
}
export function updateVideoGenerationWorkflow(generationId, workflowStatus, options = {}) {
  return request({ ...options, url: '/api/video/generation/workflow', method: 'post',
    data: { generationId, workflowStatus }, silent: true })
}

export function getImageCategories(scriptId) {
  return request({ url: '/api/video/image/categories', method: 'get', params: { scriptId } })
}

export function updateImageRecordCategory(recordId, categoryId) {
  return request({ url: `/api/video/image/records/${recordId}/category`, method: 'put', data: { categoryId } })
}

export function getImageVersions(recordKey) {
  return request({ url: `/api/video/image/records/${encodeURIComponent(recordKey)}/versions`, method: 'get' })
}

export function saveImageVersion(recordKey, data) {
  return request({ url: `/api/video/image/records/${encodeURIComponent(recordKey)}/versions`, method: 'post', data })
}

export function selectImageVersion(recordKey, versionId) {
  return request({ url: `/api/video/image/records/${encodeURIComponent(recordKey)}/versions/display`, method: 'put', data: { versionId } })
}

export function getVideoReferenceUploadResult(queueId, options = {}) {
  return request({ ...options, url: '/api/file/minio/video-reference/upload-result', method: 'get', params: { queueId }, silent: true })
}
